import { normalizeCrmActionHref } from "./safe-action-hrefs";

export type CrmTaskChecklistTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmTaskChecklistItem {
  key: string;
  label: string;
  description: string | null;
  status: string | null;
  required: boolean;
  href: string | null;
  actionLabel: string | null;
  tone: CrmTaskChecklistTone;
}

export interface CrmTaskExecutionChecklist {
  key: string;
  label: string;
  description: string | null;
  source: "configured";
  items: CrmTaskChecklistItem[];
}

export type CrmTaskExecutionChecklistSummaryTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmTaskExecutionChecklistSummary {
  checklistCount: number;
  itemCount: number;
  requiredItemCount: number;
  openRequiredItemCount: number;
  dangerItemCount: number;
  warningItemCount: number;
  tone: CrmTaskExecutionChecklistSummaryTone;
}

export interface BuildCrmTaskExecutionChecklistsOptions {
  checklistKeys?: readonly string[];
  maxChecklists?: number;
  maxItemsPerChecklist?: number;
}

export const CRM_TASK_EXECUTION_CHECKLIST_FIELD_KEYS = [
  "crmTaskExecutionChecklists",
  "taskExecutionChecklists",
  "taskChecklists",
  "operatorChecklists",
  "callChecklists",
  "completionChecklists",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 180;

export function buildCrmTaskExecutionChecklists(
  taskRecords: unknown,
  options: BuildCrmTaskExecutionChecklistsOptions = {},
): CrmTaskExecutionChecklist[] {
  const records = toRecords(taskRecords);
  if (records.length === 0) return [];

  const checklistKeys = options.checklistKeys?.length
    ? [...options.checklistKeys]
    : [...CRM_TASK_EXECUTION_CHECKLIST_FIELD_KEYS];
  const maxChecklists = normalizePositiveInteger(options.maxChecklists, 6);
  const maxItemsPerChecklist = normalizePositiveInteger(options.maxItemsPerChecklist, 10);

  return records
    .flatMap((record) => checklistKeys.flatMap((key) => parseConfiguredChecklists(key, record[key], maxItemsPerChecklist)))
    .filter((checklist) => checklist.items.length > 0)
    .slice(0, maxChecklists);
}

export function summarizeCrmTaskExecutionChecklists(
  checklists: readonly CrmTaskExecutionChecklist[],
): CrmTaskExecutionChecklistSummary | null {
  const items = checklists.flatMap((checklist) => checklist.items);
  if (items.length === 0) return null;

  const requiredItemCount = items.filter((item) => item.required).length;
  const openRequiredItemCount = items.filter((item) => item.required && !isChecklistItemComplete(item)).length;
  const dangerItemCount = items.filter((item) => item.tone === "danger").length;
  const warningItemCount = items.filter((item) => item.tone === "warning").length;
  const tone = dangerItemCount > 0
    ? "danger"
    : openRequiredItemCount > 0 || warningItemCount > 0
      ? "warning"
      : requiredItemCount > 0
        ? "success"
        : "info";

  return {
    checklistCount: checklists.length,
    itemCount: items.length,
    requiredItemCount,
    openRequiredItemCount,
    dangerItemCount,
    warningItemCount,
    tone,
  };
}

function parseConfiguredChecklists(
  rootKey: string,
  value: unknown,
  maxItemsPerChecklist: number,
): CrmTaskExecutionChecklist[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseChecklist(`${rootKey}.${index}`, entry, maxItemsPerChecklist))
      .filter((checklist): checklist is CrmTaskExecutionChecklist => Boolean(checklist));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasChecklistShape(record)) {
    const checklist = parseChecklist(rootKey, record, maxItemsPerChecklist);
    return checklist ? [checklist] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseChecklist(key, entry, maxItemsPerChecklist))
    .filter((checklist): checklist is CrmTaskExecutionChecklist => Boolean(checklist));
}

function parseChecklist(
  fallbackKey: string,
  value: unknown,
  maxItemsPerChecklist: number,
): CrmTaskExecutionChecklist | null {
  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);
  const description = truncate(readString(record.description) ?? readString(record.summary));
  const rawItems = record.items ?? record.steps ?? record.checks ?? record.requirements ?? record.verification ?? null;
  const items = parseChecklistItems(rawItems ?? record, maxItemsPerChecklist);

  return {
    key,
    label,
    description,
    source: "configured",
    items,
  };
}

function parseChecklistItems(value: unknown, maxItems: number): CrmTaskChecklistItem[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseChecklistItem(`item-${index + 1}`, entry))
      .filter((item): item is CrmTaskChecklistItem => Boolean(item))
      .slice(0, maxItems);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredChecklistKeys = new Set([
    "key",
    "label",
    "title",
    "description",
    "summary",
    "items",
    "steps",
    "checks",
    "requirements",
    "verification",
  ]);

  return Object.entries(record)
    .filter(([key]) => !ignoredChecklistKeys.has(key))
    .map(([key, entry]) => parseChecklistItem(key, entry))
    .filter((item): item is CrmTaskChecklistItem => Boolean(item))
    .slice(0, maxItems);
}

function parseChecklistItem(fallbackKey: string, value: unknown): CrmTaskChecklistItem | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? readString(record.label) ?? readString(record.title) ?? readString(record.name) ?? formatLabel(key)
    : formatLabel(key);
  const description = record
    ? truncate(readString(record.description) ?? readString(record.helpText) ?? readString(record.evidence) ?? readString(record.summary))
    : formatValue(value);
  const status = record
    ? truncate(readString(record.status) ?? readString(record.state) ?? readString(record.outcome))
    : null;
  const href = record
    ? normalizeCrmActionHref(readString(record.href) ?? readString(record.url) ?? readString(record.to))
    : null;
  const actionLabel = record
    ? readString(record.actionLabel) ?? readString(record.ctaLabel) ?? readString(record.linkLabel)
    : null;
  const hasContent = Boolean(description || status || href || record?.required === true);
  if (!hasContent) return null;

  return {
    key,
    label,
    description,
    status,
    required: record?.required === true,
    href,
    actionLabel,
    tone: normalizeTone(record ? readString(record.tone) ?? status : null),
  };
}

function hasChecklistShape(record: Record<string, unknown>): boolean {
  return ["label", "title", "description", "summary", "items", "steps", "checks", "requirements", "verification"]
    .some((key) => key in record);
}

function normalizeTone(value: string | null): CrmTaskChecklistTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "blocked" || value === "failed" || value === "overdue" || value === "missing" || value === "critical") return "danger";
  if (value === "pending" || value === "required" || value === "needs_review" || value === "partial" || value === "in_progress") return "warning";
  if (value === "complete" || value === "completed" || value === "done" || value === "verified" || value === "passed") return "success";
  if (value === "optional" || value === "review" || value === "ready") return "info";
  return "neutral";
}

function isChecklistItemComplete(item: CrmTaskChecklistItem): boolean {
  if (item.tone === "success") return true;
  const status = item.status?.trim().toLowerCase().replace(/[\s-]+/g, "_") ?? "";
  return status === "complete"
    || status === "completed"
    || status === "done"
    || status === "verified"
    || status === "passed"
    || status === "resolved"
    || status === "closed";
}

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function toRecords(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value
      .map(asRecord)
      .filter((record): record is Record<string, unknown> => Boolean(record));
  }
  const record = asRecord(value);
  return record ? [record] : [];
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
